import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, BarChart3, LogOut } from 'lucide-react';
import { useAuthStore, useAppStore } from '@/store';
import { useState, useRef, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { Badge, Card, CardContent, TextView } from 'ui-common-components';
import { DashboardShell, AppTopbar } from 'ui-common-components/shell';
import { menuConfigApi } from '@/api/client';
import type { MenuConfigItem } from '@/types';
import {
  ALL_NAV_ITEMS,
  filterNavForUser,
  mergeNavItemsWithConfig,
  pathToActiveNavId,
  resolvePageTitle,
  toAppSidebarNavItems,
} from '@/config/navigation';

// ─── Page title map ──────────────────────────────────────────────────────────

const pageTitles: Record<string, string> = {
  '': 'Dashboard',
  'chart-of-accounts': 'Chart of Accounts',
  'trial-balances': 'Trial Balances',
  'old-consolidation': 'Old Consolidation',
  reports: 'Reports',
  settings: 'Settings',
  audit: 'Audit Trail',
  'consolidation-rules': 'Rules',
  'valid-intersections': 'Valid Intersections',
  'application-cubes': 'Application Cubes',
  dimensions: 'Dimension Manager',
  data: 'Data',
  forms: 'Forms',
  'manage-ownership': 'Manage Ownership',
  'entity-ownership-structure': 'Ownership Structure',
  'journal-entries': 'Journal Entries',
  journals: 'Journals: Manage',
  'menu-control': 'Menu Control',
  'report-builder': 'Report Builder',
  footnotes: 'Footnote Manager',
  'validation-exceptions': 'Validation Exceptions',
  sod: 'Segregation of Duties',
};

// ─── Layout Component ────────────────────────────────────────────────────────

export default function Layout() {
  const { user, logout } = useAuthStore();
  const { sidebarCollapsed, setSidebarCollapsed, notifications, markNotificationRead } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuConfig, setMenuConfig] = useState<MenuConfigItem[] | null>(null);
  const [menuLoading, setMenuLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifPanelRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    menuConfigApi
      .getConfig()
      .then((res) => setMenuConfig(res.items))
      .catch(() => setMenuConfig([]))
      .finally(() => setMenuLoading(false));
  }, []);

  const sidebarNavItems = useMemo(() => {
    const merged = mergeNavItemsWithConfig(ALL_NAV_ITEMS, menuConfig, menuLoading);
    const visible = filterNavForUser(merged, user);
    return toAppSidebarNavItems(visible);
  }, [menuConfig, menuLoading, user]);

  const currentSegment = location.pathname.split('/').filter(Boolean)[0] || '';
  const pageTitle =
    pageTitles[currentSegment] ?? resolvePageTitle(location.pathname);
  const activeNavId = pathToActiveNavId(location.pathname);

  useEffect(() => {
    if (!showNotifications) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (notifPanelRef.current?.contains(t)) return;
      if (t.closest('.app-topbar__action-wrap')) return;
      setShowNotifications(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [showNotifications]);

  return (
    <>
      <DashboardShell
        pinTopbar
        sidebar={{
          fixed: true,
          responsive: true,
          showCollapseToggle: true,
          collapsed: sidebarCollapsed,
          onCollapsedChange: setSidebarCollapsed,
          closeOnNavigateWhenNarrow: true,
          header: {
            title: 'ConsolidateAI',
            subtitle: 'Financial Consolidation',
            icon: <BarChart3 className="h-6 w-6 text-[var(--sidebar-text)]" aria-hidden />,
          },
          sections: [
            {
              id: 'main',
              tier: 'primary',
              grouping: 'spacing',
              items: sidebarNavItems,
            },
          ],
          activeItemId: activeNavId,
          onItemSelect: (id) => navigate(`/${id}`),
          tokens: {
            noir0: 'var(--color-brand-primary)',
            noir1: 'color-mix(in srgb, var(--color-brand-primary) 94%, black)',
            noir2: 'color-mix(in srgb, var(--color-brand-primary) 88%, black)',
            noir3: 'color-mix(in srgb, var(--color-brand-primary) 82%, black)',
            sidebarText: 'var(--color-bg-surface)',
            sidebarTextMuted: 'color-mix(in srgb, var(--color-bg-surface) 55%, transparent)',
            sidebarHover: 'color-mix(in srgb, var(--color-bg-surface) 8%, transparent)',
            sidebarActiveBg: 'color-mix(in srgb, var(--color-bg-surface) 12%, transparent)',
            sidebarActiveTint: 'color-mix(in srgb, var(--color-bg-surface) 4%, transparent)',
            sidebarFocus: 'var(--color-focus-ring)',
            sidebarBorder: 'color-mix(in srgb, var(--color-bg-surface) 8%, transparent)',
            sidebarBorderStrong: 'color-mix(in srgb, var(--color-bg-surface) 10%, transparent)',
            sidebarNavIcon: 'color-mix(in srgb, var(--color-bg-surface) 55%, transparent)',
            sidebarNavIconActive: 'var(--color-bg-surface)',
            sidebarUtilityIcon: 'color-mix(in srgb, var(--color-bg-surface) 55%, transparent)',
            sidebarCreateDash: 'color-mix(in srgb, var(--color-bg-surface) 12%, transparent)',
          },
          user: {
            name: user?.full_name || 'User',
            role: user?.role || 'viewer',
            avatarProps: {
              name: user?.full_name || 'User',
              size: 32,
              status: 'online',
            },
            onClick: () => navigate('/settings'),
            profileRowLabel: 'Account',
          },
          footerActions: [
            {
              id: 'logout',
              icon: <LogOut className="h-4 w-4" aria-hidden />,
              label: 'Sign out',
              onClick: () => logout(),
            },
          ],
        }}
        topbar={
          <AppTopbar
            theme="light"
            title={pageTitle}
            titleAs="h1"
            responsive
            collapseCenterBelowWidth={900}
            onMobileMenuClick={() => setSidebarCollapsed(false)}
            actions={[
              {
                id: 'notifications',
                icon: <Bell className="h-5 w-5" aria-hidden />,
                label: 'Notifications',
                badgeCount: unreadCount > 0 ? unreadCount : undefined,
                onClick: () => setShowNotifications((s) => !s),
              },
            ]}
            profile={{
              name: user?.full_name || 'User',
              menuLabel: 'Account',
              onClick: () => navigate('/settings'),
              avatarProps: {
                name: user?.full_name || 'User',
                size: 32,
                status: 'online',
              },
            }}
            tokens={{
              border: 'var(--color-border-subtle)',
              bg: 'color-mix(in srgb, var(--color-bg-surface) 88%, transparent)',
            }}
          />
        }
        contentClassName="min-h-0 min-w-0 flex-1 basis-0 overflow-y-auto overscroll-y-contain animate-fade-in"
        contentStyle={{ padding: 'var(--layout-container-padding)' }}
      >
        <Outlet />
      </DashboardShell>

      {showNotifications ? (
        <div
          ref={notifPanelRef}
          className="fixed z-[200] w-80 max-w-[calc(100vw-32px)] animate-scale-in"
          style={{ top: '56px', right: '24px' }}
        >
          <Card className="overflow-hidden shadow-lg" style={{ boxShadow: 'var(--shadow-md)' }}>
            <CardContent className="p-0">
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: 'var(--border-thin) var(--color-border-subtle)' }}
              >
                <TextView variant="secondary" fontWeight="semibold">
                  Notifications
                </TextView>
                {unreadCount > 0 ? (
                  <Badge variant="neutral" tone="soft" size="sm">
                    {unreadCount} new
                  </Badge>
                ) : null}
              </div>
              <div className="max-h-80 overflow-y-auto scrollbar-thin">
                {notifications.length === 0 ? (
                  <div className="px-4 py-10 text-center">
                    <TextView variant="secondary" color="hint">
                      No notifications yet
                    </TextView>
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notif) => (
                    <div
                      key={notif.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => markNotificationRead(notif.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          markNotificationRead(notif.id);
                        }
                      }}
                      className={clsx('cursor-pointer px-4 py-3 transition-colors duration-150')}
                      style={{
                        borderBottom: 'var(--border-thin) var(--color-border-subtle)',
                        background: !notif.read
                          ? 'color-mix(in srgb, var(--color-surface-mist) 35%, transparent)'
                          : undefined,
                      }}
                    >
                      <div className="flex items-start gap-2.5">
                        <div
                          className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full"
                          style={{
                            background:
                              notif.type === 'success'
                                ? 'var(--color-state-success)'
                                : notif.type === 'error'
                                  ? 'var(--color-state-error)'
                                  : notif.type === 'warning'
                                    ? 'var(--color-state-warning)'
                                    : 'var(--color-brand-primary)',
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <TextView variant="secondary" fontWeight="medium" truncate>
                            {notif.title}
                          </TextView>
                          <TextView variant="small" color="secondary" className="mt-0.5 line-clamp-2">
                            {notif.message}
                          </TextView>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </>
  );
}
